/**
 * Hooks pour les établissements scolaires
 * Encapsulent la logique de data fetching avec React Query
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { establishmentsService } from '../services';
import type {
  Establishment,
  CreateEstablishmentDto,
  UpdateEstablishmentDto,
} from '@sphere/school/domain';

import type { EstablishmentStats } from '@sphere/school/domain';

// ============================================
// Query Keys (pour le cache)
// ============================================

export const establishmentKeys = {
  all: ['establishments'] as const,
  lists: () => [...establishmentKeys.all, 'list'] as const,
  list: (filters?: Record<string, any>) =>
    [...establishmentKeys.lists(), filters] as const,
  details: () => [...establishmentKeys.all, 'detail'] as const,
  detail: (id: string) => [...establishmentKeys.details(), id] as const,
  stats: (id: string) => [...establishmentKeys.detail(id), 'stats'] as const,
  withDetails: (id: string) =>
    [...establishmentKeys.detail(id), 'with-details'] as const,
};

// ============================================
// Queries
// ============================================

/**
 * Hook pour récupérer tous les établissements accessibles
 * Backend filtre automatiquement selon le scope utilisateur
 *
 * @example
 * ```tsx
 * const { data: establishments, isLoading } = useEstablishments();
 * ```
 */
export function useEstablishments(
  options?: Omit<UseQueryOptions<Establishment[]>, 'queryKey' | 'queryFn'>
) {
  return useQuery<Establishment[]>({
    queryKey: establishmentKeys.lists(),
    queryFn: () => establishmentsService.getEstablishments(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
}

/**
 * Hook pour récupérer un établissement par ID
 *
 * @example
 * ```tsx
 * const { data: establishment } = useEstablishment(establishmentId);
 * ```
 */
export function useEstablishment(
  id: string | undefined,
  options?: Omit<UseQueryOptions<Establishment>, 'queryKey' | 'queryFn'>
) {
  return useQuery<Establishment>({
    queryKey: establishmentKeys.detail(id!),
    queryFn: () => establishmentsService.getEstablishment(id!),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

/**
 * Hook pour récupérer un établissement avec toutes ses données enrichies
 * (classes, stats, etc.)
 *
 * @example
 * ```tsx
 * const { data } = useEstablishmentWithDetails(establishmentId);
 * // data = { establishment, stats, classes }
 * ```
 */
export function useEstablishmentWithDetails(
  id: string | undefined,
  options?: Omit<
    UseQueryOptions<{
      establishment: Establishment;
      stats: EstablishmentStats;
      classes: any[];
    }>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery({
    queryKey: establishmentKeys.withDetails(id!),
    queryFn: () => establishmentsService.getEstablishmentWithDetails(id!),
    enabled: !!id,
    staleTime: 2 * 60 * 1000, // 2 minutes (données plus volatiles)
    ...options,
  });
}

/**
 * Hook pour récupérer les statistiques d'un établissement
 *
 * @example
 * ```tsx
 * const { data: stats } = useEstablishmentStats(establishmentId);
 * // stats = { totalClasses, totalStudents, totalTeachers, totalSubjects }
 * ```
 */
export function useEstablishmentStats(
  id: string | undefined,
  options?: Omit<UseQueryOptions<EstablishmentStats>, 'queryKey' | 'queryFn'>
) {
  return useQuery<EstablishmentStats>({
    queryKey: establishmentKeys.stats(id!),
    queryFn: async () => {
      const details = await establishmentsService.getEstablishmentWithDetails(
        id!
      );
      return details.stats;
    },
    enabled: !!id,
    staleTime: 2 * 60 * 1000,
    ...options,
  });
}

/**
 * Hook pour récupérer les établissements de l'utilisateur connecté
 * Filtre basé sur le scope du bootstrap
 *
 * @example
 * ```tsx
 * const { data: myEstablishments } = useUserEstablishments();
 * ```
 */
export function useUserEstablishments(
  userEstablishmentIds: string[] | null,
  options?: Omit<UseQueryOptions<Establishment[]>, 'queryKey' | 'queryFn'>
) {
  return useQuery<Establishment[]>({
    queryKey: [...establishmentKeys.lists(), { user: userEstablishmentIds }],
    queryFn: () =>
      establishmentsService.getUserEstablishments(userEstablishmentIds),
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

/**
 * Hook pour l'établissement actif
 * Passez l'ID depuis useActiveEstablishmentId() (de @sphere/auth/data-access)
 *
 * @example
 * ```tsx
 * const establishmentId = useActiveEstablishmentId();
 * const { data: activeEstablishment } = useActiveEstablishment(establishmentId ?? undefined);
 * ```
 */
export function useActiveEstablishment(
  establishmentId: string | undefined,
  options?: Omit<UseQueryOptions<Establishment>, 'queryKey' | 'queryFn'>
) {
  return useEstablishment(establishmentId, options);
}

// ============================================
// Mutations
// ============================================

/**
 * Hook pour créer un établissement
 * Invalide automatiquement le cache
 *
 * @example
 * ```tsx
 * const createMutation = useCreateEstablishment();
 *
 * const handleCreate = async () => {
 *   await createMutation.mutateAsync(dto);
 * };
 * ```
 */
export function useCreateEstablishment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: CreateEstablishmentDto) =>
      establishmentsService.createEstablishment(dto),
    onSuccess: () => {
      // Invalider le cache des listes
      queryClient.invalidateQueries({ queryKey: establishmentKeys.lists() });
    },
  });
}

/**
 * Hook pour mettre à jour un établissement
 *
 * @example
 * ```tsx
 * const updateMutation = useUpdateEstablishment();
 *
 * const handleUpdate = async () => {
 *   await updateMutation.mutateAsync({ id: establishmentId, dto });
 * };
 * ```
 */
export function useUpdateEstablishment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: UpdateEstablishmentDto }) =>
      establishmentsService.updateEstablishment(id, dto),
    onSuccess: (_, variables) => {
      // Invalider le cache de l'établissement modifié
      queryClient.invalidateQueries({
        queryKey: establishmentKeys.detail(variables.id),
      });
      // Invalider les listes
      queryClient.invalidateQueries({ queryKey: establishmentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ['my-establishment'] });
    },
  });
}

/**
 * Hook pour supprimer un établissement
 *
 * @example
 * ```tsx
 * const deleteMutation = useDeleteEstablishment();
 *
 * const handleDelete = async () => {
 *   await deleteMutation.mutateAsync(establishmentId);
 * };
 * ```
 */
export function useDeleteEstablishment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => establishmentsService.deleteEstablishment(id),
    onSuccess: (_, id) => {
      // Invalider le cache de l'établissement supprimé
      queryClient.invalidateQueries({
        queryKey: establishmentKeys.detail(id),
      });
      // Invalider les listes
      queryClient.invalidateQueries({ queryKey: establishmentKeys.lists() });
    },
  });
}
